import type { PdpOrderBy } from '@vizit/api';
import type { ScoreLevel } from '@vizit/organisms';
import { createContext } from 'react';
import type { ProductTableState } from './product-table-provider.state';

interface ProductTableContextType extends ProductTableState {
  /**
   * The ID of the organization to fetch products for
   *
   * @required
   * @type {string}
   */
  organizationId: string;
  /**
   * The ID of the portfolio to fetch products for
   *
   * @optional
   * @type {string}
   */
  workspaceId?: string;
  /**
   * Function to filter by brand
   *
   * @param brand - Brand name to filter by
   * @type {(brand: string) => void}
   */
  onFilterBrand: (brand: string) => void;
  /**
   * Function to filter by product category
   *
   * @param productCategoryId - Vizit internal product category UUID
   * @param productCategoryName - Product category name (for display)
   * @param categoryId - Retailer category ID string (used when retailers feature is on)
   * @type {(productCategoryId: string, productCategoryName: string, categoryId?: string) => void}
   */
  onFilterProductCategory: (productCategoryId: string, productCategoryName: string, categoryId?: string) => void;
  /**
   * Function to filter by listing score level
   *
   * @param listingScoreLevel - Score level to filter by
   * @type {(listingScoreLevel: ScoreLevel) => void}
   */
  onFilterListingScoreLevel: (listingScoreLevel: ScoreLevel) => void;
  /**
   * Function to update the unified search term (searches name OR item_id)
   *
   * @param value - Search term
   * @type {(value: string) => void}
   */
  onSearchTermChange: (value: string) => void;
  /**
   * Function to filter by retailer
   *
   * @param retailer - Retailer region id to filter by, or undefined to clear
   * @type {(retailer: string | undefined) => void}
   */
  onFilterRetailer: (retailer: string | undefined) => void;
  /**
   * Function to sort by a field. Toggles: desc → asc → default on repeated calls.
   *
   * @param field - The field to sort by
   * @type {(field: PdpOrderBy['field']) => void}
   */
  onSort: (field: PdpOrderBy['field']) => void;
  /**
   * Function to toggle the experiment (score-changed) filter.
   *
   * @param enabled - Whether to show only products whose score changed
   * @type {(enabled: boolean) => void}
   */
  onFilterExperiment: (enabled: boolean) => void;
}

const ProductTableContext = createContext<ProductTableContextType | null>(null);

export { ProductTableContext };
export type { ProductTableContextType };
